import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://us-central1-cura-a5c8a.cloudfunctions.net/api';

async function apiFetch(path, options = {}) {
  const token = await getAuth().currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const SUB_TABS = ['Reports', 'All Posts'];

export default function CommunityTab() {
  const [subTab, setSubTab] = useState('Reports');
  const [reports, setReports] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hideReason, setHideReason] = useState('');
  const [moderating, setModerating] = useState(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/community/admin/reports?limit=100');
      setReports(data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/community/posts?sort=new&limit=50');
      setPosts(data.data?.posts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === 'Reports') loadReports();
    else loadPosts();
  }, [subTab, loadReports, loadPosts]);

  async function moderate(targetType, targetId, action, reason) {
    setModerating(`${targetType}_${targetId}_${action}`);
    try {
      await apiFetch('/api/community/admin/moderation', {
        method: 'POST',
        body: JSON.stringify({ targetType, targetId, action, reason: reason || '', clearReports: true }),
      });
      if (subTab === 'Reports') await loadReports();
      else await loadPosts();
    } catch (e) {
      alert('Moderation action failed: ' + e.message);
    } finally {
      setModerating(null);
    }
  }

  const filteredReports = reports.filter(r =>
    !search || [r.previewTitle, r.previewBody, r.authorDisplayName].join(' ').toLowerCase().includes(search.toLowerCase())
  );
  const filteredPosts = posts.filter(p =>
    !search || [p.title, p.body, p.authorDisplayName].join(' ').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={s.pageHeader}>
        <h2 style={s.pageTitle}>Community</h2>
        <p style={s.pageSubtitle}>Moderate posts and manage reported content</p>
      </div>

      <div style={s.subTabRow}>
        {SUB_TABS.map(t => (
          <button key={t} style={{ ...s.subTab, ...(subTab === t ? s.subTabActive : {}) }} onClick={() => setSubTab(t)}>
            {t}
            {t === 'Reports' && reports.filter(r => !r.isHidden).length > 0 && (
              <span style={s.badge}>{reports.filter(r => !r.isHidden).length}</span>
            )}
          </button>
        ))}
      </div>

      <div style={s.filters}>
        <input style={s.searchInput} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <button style={s.refreshBtn} onClick={subTab === 'Reports' ? loadReports : loadPosts}>↺ Refresh</button>
      </div>

      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : subTab === 'Reports' ? (
        filteredReports.length === 0 ? (
          <div style={s.empty}>No reports. Community looks clean! ✓</div>
        ) : (
          <div style={s.list}>
            {filteredReports.map(item => (
              <ReportCard key={`${item.targetType}_${item.targetId}`} item={item} onModerate={moderate} moderating={moderating} />
            ))}
          </div>
        )
      ) : (
        filteredPosts.length === 0 ? (
          <div style={s.empty}>No posts found.</div>
        ) : (
          <div style={s.list}>
            {filteredPosts.map(post => (
              <PostCard key={post.id} post={post} onModerate={moderate} moderating={moderating} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function ReportCard({ item, onModerate, moderating }) {
  const [reason, setReason] = useState('');
  const key = `${item.targetType}_${item.targetId}`;
  return (
    <div style={{ ...s.card, ...(item.isHidden ? s.cardHidden : {}) }}>
      <div style={s.cardTop}>
        <div style={s.cardMeta}>
          <span style={{ ...s.typeBadge, ...(item.targetType === 'post' ? s.postBadge : s.commentBadge) }}>
            {item.targetType.toUpperCase()}
          </span>
          <span style={s.reportCount}>🚨 {item.reportCount} report{item.reportCount !== 1 ? 's' : ''}</span>
          <span style={s.author}>by {item.authorDisplayName}</span>
          {item.isHidden && <span style={s.hiddenBadge}>Hidden</span>}
        </div>
        <span style={s.date}>{new Date(item.lastReportedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      </div>
      {item.previewTitle && <p style={s.postTitle}>{item.previewTitle}</p>}
      <p style={s.feedbackText}>{item.previewBody}</p>
      {item.hiddenReason && <p style={s.hiddenReason}>Reason: {item.hiddenReason}</p>}
      <div style={s.actionRow}>
        <input style={s.reasonInput} placeholder="Hide reason (optional)" value={reason} onChange={e => setReason(e.target.value)} />
        <button
          style={s.hideBtn} disabled={item.isHidden || moderating === `${item.targetType}_${item.targetId}_hide`}
          onClick={() => onModerate(item.targetType, item.targetId, 'hide', reason)}
        >Hide</button>
        <button
          style={s.unhideBtn} disabled={!item.isHidden || moderating === `${item.targetType}_${item.targetId}_unhide`}
          onClick={() => onModerate(item.targetType, item.targetId, 'unhide')}
        >Unhide</button>
        <button
          style={s.deleteBtn} disabled={moderating === `${item.targetType}_${item.targetId}_delete`}
          onClick={() => { if (window.confirm('Delete this content?')) onModerate(item.targetType, item.targetId, 'delete'); }}
        >Delete</button>
      </div>
    </div>
  );
}

function PostCard({ post, onModerate, moderating }) {
  return (
    <div style={{ ...s.card, ...(post.isHidden ? s.cardHidden : {}) }}>
      <div style={s.cardTop}>
        <div style={s.cardMeta}>
          <span style={s.author}>{post.authorDisplayName}</span>
          <span style={s.reportCount}>▲ {post.score} · 💬 {post.commentCount}</span>
          {post.isHidden && <span style={s.hiddenBadge}>Hidden</span>}
        </div>
        <span style={s.date}>{new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      </div>
      <p style={s.postTitle}>{post.title}</p>
      <p style={s.feedbackText}>{post.body}</p>
      <div style={s.actionRow}>
        <button
          style={s.hideBtn} disabled={post.isHidden}
          onClick={() => onModerate('post', post.id, 'hide', '')}
        >Hide</button>
        <button
          style={s.unhideBtn} disabled={!post.isHidden}
          onClick={() => onModerate('post', post.id, 'unhide')}
        >Unhide</button>
        <button
          style={s.deleteBtn}
          onClick={() => { if (window.confirm('Delete this post?')) onModerate('post', post.id, 'delete'); }}
        >Delete</button>
      </div>
    </div>
  );
}

const s = {
  pageHeader: { marginBottom: 24 },
  pageTitle: { fontSize: 24, fontWeight: 800, color: 'var(--green-900)' },
  pageSubtitle: { fontSize: 14, color: 'var(--text-muted)', marginTop: 4 },
  subTabRow: { display: 'flex', gap: 8, marginBottom: 20 },
  subTab: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px',
    background: 'white', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
    fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)',
  },
  subTabActive: { background: 'var(--green-800)', color: 'white', border: '1.5px solid var(--green-800)' },
  badge: { background: '#ef5350', color: 'white', fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 999 },
  filters: { display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' },
  searchInput: {
    flex: 1, padding: '10px 14px', borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--border)', background: 'white', fontSize: 14,
  },
  refreshBtn: { padding: '10px 16px', background: 'var(--green-100)', color: 'var(--green-800)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700 },
  loading: { textAlign: 'center', padding: 48, color: 'var(--text-muted)' },
  empty: { textAlign: 'center', padding: 48, color: 'var(--text-muted)' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { background: 'white', borderRadius: 'var(--radius-md)', padding: '20px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)' },
  cardHidden: { opacity: 0.6 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  typeBadge: { fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 999 },
  postBadge: { background: 'var(--green-100)', color: 'var(--green-800)' },
  commentBadge: { background: '#fff3e0', color: '#e65100' },
  reportCount: { fontSize: 13, fontWeight: 700, color: '#c62828' },
  author: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' },
  hiddenBadge: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: '#ffebee', color: '#c62828' },
  date: { fontSize: 12, color: 'var(--text-muted)' },
  postTitle: { fontSize: 15, fontWeight: 700, color: 'var(--green-900)', marginBottom: 6 },
  feedbackText: { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 },
  hiddenReason: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 },
  actionRow: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 },
  reasonInput: {
    flex: 1, minWidth: 140, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--border)', background: 'var(--green-50)', fontSize: 13,
  },
  hideBtn: { padding: '8px 14px', background: 'var(--green-800)', color: 'white', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700 },
  unhideBtn: { padding: '8px 14px', background: 'var(--green-100)', color: 'var(--green-800)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700 },
  deleteBtn: { padding: '8px 14px', background: '#ffebee', color: '#c62828', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700 },
};
